import { Checkbox, FormControlLabel, FormGroup, IconButton, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { useIsMobile } from '@uplift/hooks';
import { Button, Modal, ModalActions, ModalTitle, ScrollableView } from '@uplift/ui-v2';
import { useField } from 'formik';
import { useCallback, useState } from 'react';

export type CheckboxesFormRowProps<T> = {
  title: string;
  modalTitle?: string;
  options: T[];
  getLabel: (option: T) => string;
  getValue: (option: T) => string;
  name: string;
  groupBy?: (option: T) => string;
  disableModalVariant?: boolean;
};

export const CheckboxesFormRow = <T extends object>(props: CheckboxesFormRowProps<T>) => {
  const isMobile = useIsMobile();
  const { classes } = useCheckboxesFormRowStyles();
  const [ open, setOpen ] = useState(false);
  const [ field ] = useField<string[]>(props.name);

  const selectedOptions = props.options.filter(option => field.value.includes(props.getValue(option)));

  return (
    <div className={classes.rowContainer}>
      <div className={classes.row}>
        <div>
          <Typography>
            {props.title}
            {!!selectedOptions.length && ` (${selectedOptions.length})`}
          </Typography>
          {!!selectedOptions.length && (
            <Typography className={classes.rowValue}>
              {selectedOptions.map(option => props.getLabel(option)).join(', ')}
            </Typography>
          )}
        </div>
        <Trigger
          onToggle={() => setOpen(p => !p)}
          open={open}
          valuesLength={field.value.length}
        />
      </div>

      {open && !isMobile && !props.disableModalVariant && <CheckboxesForm {...props} />}

      <Modal fullScreen open={open && isMobile && !props.disableModalVariant}>
        <ModalTitle className={classes.modalTitle}>
          {props.modalTitle ?? props.title}
        </ModalTitle>
        <ScrollableView>
          <CheckboxesForm {...props} />
        </ScrollableView>
        <ModalActions className={classes.modalActions}>
          <Button color="secondary" variant="contained" onClick={() => setOpen(false)}>
            Done
          </Button>
        </ModalActions>
      </Modal>
    </div>
  );
};

const useCheckboxesFormRowStyles = makeStyles({ name: 'CheckboxesFormRow' })(theme => ({
  modalTitle: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  modalActions: {
    borderTop: `1px solid ${theme.palette.divider}`,
    '& button': {
      flex: 1
    }
  },
  rowContainer: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  row: {
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(1.5, 2),
    }
  },
  rowValue: {
    marginTop: theme.spacing(0.5),
    fontWeight: 500,
  },
}));

type TriggerProps = {
  onToggle: () => void;
  open: boolean;
  valuesLength: number;
};

const Trigger: React.VFC<TriggerProps> = (props) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Button color="secondary" onClick={props.onToggle}>
        {props.valuesLength ? 'Edit' : 'Add'}
      </Button>
    );
  }

  return (
    <IconButton color="secondary" onClick={props.onToggle}>
      {props.open ? <ExpandLess /> : <ExpandMore />}
    </IconButton>
  );
};

const CheckboxesForm = <T extends object>(props: CheckboxesFormRowProps<T>) => {
  const [ field, _meta, helpers ] = useField<string[]>(props.name);
  const { classes } = useCheckboxesFormStyles();

  const onSelectOption = useCallback((value: string, checked: boolean) => {
    helpers.setValue(checked ? [ ...field.value, value ] : field.value.filter(v => v !== value), true);
    helpers.setTouched(true, false);
  }, [ field.value, helpers ]);

  return (
    <div className={classes.root}>
      <FormGroup className={classes.formGroup}>
        {props.options.map((option) => {
          const value = props.getValue(option);
          const label = props.getLabel(option);

          return (
            <FormControlLabel
              className={classes.controlLabel}
              label={label}
              key={value}
              value={value}
              checked={field.value.includes(value)}
              onChange={(_, checked) => onSelectOption(value, checked)}
              control={<Checkbox color="secondary" />}
            />
          );
        }
        )}
      </FormGroup>
    </div>
  );
};

const useCheckboxesFormStyles = makeStyles({ name: 'CheckboxesForm' })(theme => ({
  root: {
    padding: theme.spacing(3, 2),

    [theme.breakpoints.up('md')]: {
      backgroundColor: theme.palette.grey[100],
      padding: theme.spacing(2),
    }
  },
  controlLabel: {
    marginLeft: 'unset',
  },
  formGroup: {
    backgroundColor: theme.palette.background.paper,
  }
}));
